import { Icon } from "../../components/Icon";
import { useMarkdownStyle } from "../../components/markdown/MarkdownStyleContext";
import { markdownParagraphSize } from "../../components/markdown/markdownSizes";
import { sameNodePosition, } from "../../components/markdown/utils";
import { Check } from "../../icons/v2-stroke";
import { cn } from "../../lib";
import { assertNever } from "../../lib/utils";
import { cva } from "class-variance-authority";
import React, { createContext, memo, useContext } from "react";
// Ordered lists share their `start` so step badges can number task items.
const OlStartContext = createContext(1);
export const ulBlockVariants = cva("pb-2 flex flex-col gap-1", {
    variants: {
        taskList: {
            false: "list-disc pl-6",
            true: "",
        },
    },
    defaultVariants: {
        taskList: false,
    },
});
// remark-gfm marks lists holding task items with the `contains-task-list` class. react-markdown
// gives list items a `checked` prop, but the list element itself only carries this class.
function isTaskList(className) {
    var _a;
    return (_a = className === null || className === void 0 ? void 0 : className.includes("contains-task-list")) !== null && _a !== void 0 ? _a : false;
}
/**
 * Renders unordered lists inside Markdown output; GitHub Flavored Markdown
 * task lists drop the disc bullets.
 * @summary Unordered-list renderer for Markdown.
 */
export const UlBlock = memo(({ children, className }) => {
    const { textColor, forcedTextSize } = useMarkdownStyle();
    const textSize = forcedTextSize !== null && forcedTextSize !== void 0 ? forcedTextSize : markdownParagraphSize;
    return (React.createElement("ul", { className: cn(ulBlockVariants({ taskList: isTaskList(className) }), textColor, textSize, className) }, children));
}, (prev, next) => sameNodePosition(prev.node, next.node) && prev.className === next.className);
UlBlock.displayName = "UlBlock";
export const olBlockVariants = cva("pb-2 flex flex-col gap-1", {
    variants: {
        taskList: {
            false: "list-decimal pl-6",
            true: "",
        },
    },
    defaultVariants: {
        taskList: false,
    },
});
/**
 * Renders ordered lists inside Markdown output, honoring the `start` number
 * from the source Markdown; task lists drop the decimal markers and indent,
 * like UlBlock does for bullets.
 * @summary Ordered-list renderer for Markdown.
 */
export const OlBlock = memo(({ children, className, start }) => {
    const { textColor, forcedTextSize } = useMarkdownStyle();
    const textSize = forcedTextSize !== null && forcedTextSize !== void 0 ? forcedTextSize : markdownParagraphSize;
    return (React.createElement(OlStartContext.Provider, { value: start !== null && start !== void 0 ? start : 1 },
        React.createElement("ol", { start: start, className: cn(olBlockVariants({ taskList: isTaskList(className) }), textColor, textSize, className) }, children)));
}, (prev, next) => sameNodePosition(prev.node, next.node) &&
    prev.start === next.start &&
    prev.className === next.className);
OlBlock.displayName = "OlBlock";
// mt-[3px] centers the 20px badge on the 26px leading-relaxed text line. The border stays on
// both states so ticking a task only transitions colors.
const taskStepBadgeVariants = cva(cn("mt-[3px] flex size-5 shrink-0 items-center justify-center rounded-full border", "transition-colors duration-300 motion-reduce:transition-none"), {
    variants: {
        checked: {
            true: "border-highlight-50 bg-highlight-50 text-highlight-500",
            false: "border-faint text-faint",
        },
    },
});
function taskStepLabel({ checked, number }) {
    if (checked) {
        return "Done";
    }
    return number !== undefined ? `Step ${number}` : "To do";
}
function TaskStepBadge({ checked, number }) {
    return (React.createElement("div", { role: "img", "aria-label": taskStepLabel({ checked, number }), className: taskStepBadgeVariants({ checked }) }, checked ? (React.createElement(Icon, { visual: Check, size: "2xs", className: "animate-in fade-in zoom-in-50 duration-300 motion-reduce:animate-none" })) : (number !== undefined && (React.createElement("span", { className: "text-xs font-medium leading-none" }, number)))));
}
export const liBlockVariants = cva(["break-words"]);
/**
 * Renders list items inside Markdown output; task-list items (react-markdown
 * passes them a boolean `checked`) suppress their list marker. In the "step"
 * task-list variant they get a read-only circle badge instead of a checkbox.
 * @summary List-item renderer for Markdown.
 */
export const LiBlock = memo(({ children, className, checked, index, ordered }) => {
    const { textColor, forcedTextSize, taskListVariant } = useMarkdownStyle();
    const start = useContext(OlStartContext);
    const textSize = forcedTextSize !== null && forcedTextSize !== void 0 ? forcedTextSize : markdownParagraphSize;
    const isTaskListItem = typeof checked === "boolean";
    if (isTaskListItem) {
        switch (taskListVariant) {
            case "step":
                return (React.createElement("li", { className: cn(liBlockVariants(), "flex list-none items-start gap-2", textColor, textSize, className) },
                    React.createElement(TaskStepBadge, { checked: checked, number: ordered && index !== undefined ? start + index : undefined }),
                    React.createElement("div", { className: cn("min-w-0 flex-1 transition-colors duration-300 motion-reduce:transition-none", checked && "text-faint line-through") }, children)));
            case "checkbox":
                break;
            default:
                assertNever(taskListVariant);
        }
    }
    return (React.createElement("li", { className: cn(liBlockVariants(), isTaskListItem && "list-none", textColor, textSize, className) }, children));
}, (prev, next) => sameNodePosition(prev.node, next.node) &&
    prev.className === next.className &&
    prev.checked === next.checked &&
    prev.index === next.index &&
    prev.ordered === next.ordered);
LiBlock.displayName = "LiBlock";
//# sourceMappingURL=List.js.map